import React, { FC, useCallback, useState, useEffect } from "react";
import { Redirect, useLocation, useParams } from "react-router-dom";

// Library Components
import { Button, Onboarding, OnboardingStep } from "@xapp/components";
// GraphQL
import { useGetUserProfileQuery, useGetAppInfoQuery } from "generated/graphql";
// Containers
import { PostPaymentStepContainer } from "./setup/PostPaymentStepContainer";
import { AvailabilityStepContainer } from "./setup/AvailabilityStepContainer";
import { DataCollectionStepContainer } from "./setup/DataCollectionStepContainer";
// import { AIInformationStepContainer } from "./setup/AIInformationStepContainer";
import { LeadDestinationStepContainer } from "./setup/LeadDestinationStepContainer";
import { CompletionStepContainer } from "./setup/CompletionStepContainer";

// Utils
import { getStepAside, getStepHeader } from "./V3/utils";

// Styles
import style from "./V3/OnboardingV3Container.module.scss";

function useQueryParams() {
    return new URLSearchParams(useLocation().search);
}

interface Path {
    step: string;
    nextStep?: string;
    prevStep?: string;
}

// Post-payment setup path
const SETUP_PATH: Path[] = [
    {
        step: "post_payment",
        nextStep: "availability",
    },
    {
        step: "availability",
        prevStep: "post_payment",
        nextStep: "data_collection",
    },
    {
        step: "data_collection",
        prevStep: "availability",
        // Skip AI Information step for now - go directly to lead destination
        nextStep: "lead_destination",
    },
    // AI Information step commented out for now
    // {
    //     step: "ai_information",
    //     prevStep: "data_collection",
    //     nextStep: "lead_destination",
    // },
    {
        step: "lead_destination",
        prevStep: "data_collection", // Updated to skip AI step
        nextStep: "completion",
    },
    {
        step: "completion",
        prevStep: "lead_destination",
    },
];

export const SetupContainer: FC = () => {
    const { appId } = useParams<{ appId: string }>();

    const queryParams = useQueryParams();

    // Get current step from URL or default to first step
    const [currentStepName, setCurrentStepName] = useState(() => queryParams.get("step") || "post_payment");
    const currentStep = SETUP_PATH.find((p) => p.step === currentStepName);

    const [redirectUrl, setRedirectUrl] = useState<string>();

    const [getProfile] = useGetUserProfileQuery({
        requestPolicy: "cache-and-network",
        variables: {},
    });

    const [getAppInfo] = useGetAppInfoQuery({
        pause: !appId,
        requestPolicy: "cache-and-network",
        variables: {
            appId,
        },
    });

    const { data: appData } = getAppInfo;
    const app = appData?.app; // as ClientApp | undefined;
    const organizationId = app?.organizationId;

    // Get query params

    const debug = queryParams.get("debug");

    const handleRedirect = useCallback(() => {
        // Redirect them to app home
        const successPath = `/${organizationId}/${appId}?setup=success`;

        // go to the successPath
        setRedirectUrl(successPath);
    }, [appId, organizationId]);

    // Update query params to navigate between steps
    const navigateToStep = useCallback((stepName: string) => {
        const params = new URLSearchParams(window.location.search);
        params.set("step", stepName);
        window.history.pushState({}, "", `${window.location.pathname}?${params.toString()}`);

        // Update the current step state to trigger re-render
        setCurrentStepName(stepName);
    }, []);

    // Main handler for moving to next step
    const handleNext = useCallback(() => {
        const nextStepName = currentStep?.nextStep;
        if (nextStepName) {
            navigateToStep(nextStepName);
        } else {
            // No more steps, redirect to app dashboard
            handleRedirect();
        }
    }, [currentStep, navigateToStep, handleRedirect]);

    // Handle back navigation
    const handleBack = useCallback(() => {
        const prevStepName = currentStep?.prevStep;
        if (prevStepName) {
            navigateToStep(prevStepName);
        }
    }, [currentStep, navigateToStep]);

    // Listen for browser back/forward buttons
    useEffect(() => {
        const handlePopState = () => {
            const newStep = new URLSearchParams(window.location.search).get("step") || "post_payment";
            setCurrentStepName(newStep);
        };

        window.addEventListener("popstate", handlePopState);
        return () => window.removeEventListener("popstate", handlePopState);
    }, []);

    if (redirectUrl) {
        return <Redirect to={redirectUrl} />;
    }

    // Data
    const { data } = getProfile;
    const profile = data?.profile;

    return (
        <>
            <div className={style.headerImageWrapper}>
                <img src={"/img/xapp-ai-horizontal-2color-rgb.png"} alt="logo" className={style.headerImage} />
            </div>
            <Onboarding
                steps={{
                    bottom: (
                        <div style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
                            {profile && profile.email && (
                                <div style={{ width: "100%", textAlign: "center" }}>
                                    <small>👋Hello {profile.email}! Let's finish setting up your business.</small>
                                </div>
                            )}
                        </div>
                    ),
                }}
                progressBar={{
                    type: "loading-bar",
                    rightContent: null,
                    disableBack: !currentStep?.prevStep,
                    disableNumericalProgress: true,
                    onBack: currentStep?.prevStep ? handleBack : undefined,
                }}
            >
                <OnboardingStep
                    active={currentStepName === "post_payment"}
                    done={currentStepName !== "post_payment"}
                    hide={currentStepName !== "post_payment"}
                    header={getStepHeader("post_payment", undefined, queryParams)}
                    title="Welcome!"
                >
                    <PostPaymentStepContainer
                        className={style.onboardingContent}
                        aside={getStepAside("post_payment", undefined, queryParams)}
                        onNext={handleNext}
                    />
                </OnboardingStep>

                <OnboardingStep
                    active={currentStepName === "availability"}
                    done={currentStepName !== "availability"}
                    hide={currentStepName !== "availability"}
                    header={getStepHeader("availability", undefined, queryParams)}
                    title="Availability"
                >
                    <AvailabilityStepContainer
                        className={style.onboardingContent}
                        app={app}
                        aside={getStepAside("availability", undefined, queryParams)}
                        onNext={handleNext}
                        onBack={handleBack}
                    />
                </OnboardingStep>

                <OnboardingStep
                    active={currentStepName === "data_collection"}
                    done={currentStepName !== "data_collection"}
                    hide={currentStepName !== "data_collection"}
                    header={getStepHeader("data_collection", undefined, queryParams)}
                    title="Data Collection"
                >
                    <DataCollectionStepContainer
                        className={style.onboardingContent}
                        app={app}
                        aside={getStepAside("data_collection", undefined, queryParams)}
                        onNext={handleNext}
                        onBack={handleBack}
                    />
                </OnboardingStep>

                {/* AI Information step commented out for now */}
                {/* <OnboardingStep
                    active={currentStepName === "ai_information"}
                    done={currentStepName !== "ai_information"}
                    hide={currentStepName !== "ai_information"}
                    header={getStepHeader("ai_information", undefined, queryParams)}
                    title="AI Information"
                >
                    <AIInformationStepContainer
                        className={style.onboardingContent}
                        app={app}
                        aside={getStepAside("ai_information", undefined, queryParams)}
                        onNext={handleNext}
                        onBack={handleBack}
                    />
                </OnboardingStep> */}

                <OnboardingStep
                    active={currentStepName === "lead_destination"}
                    done={currentStepName !== "lead_destination"}
                    hide={currentStepName !== "lead_destination"}
                    header={getStepHeader("lead_destination", undefined, queryParams)}
                    title="Lead Destination"
                >
                    <LeadDestinationStepContainer
                        className={style.onboardingContent}
                        app={app}
                        aside={getStepAside("lead_destination", undefined, queryParams)}
                        onNext={handleNext}
                        onBack={handleBack}
                    />
                </OnboardingStep>

                <OnboardingStep
                    active={currentStepName === "completion"}
                    done={currentStepName !== "completion"}
                    hide={currentStepName !== "completion"}
                    header={getStepHeader("completion", undefined, queryParams)}
                    title="Setup Complete"
                >
                    <CompletionStepContainer
                        className={style.onboardingContent}
                        aside={getStepAside("completion", undefined, queryParams)}
                        onNext={handleNext}
                    />
                </OnboardingStep>
            </Onboarding>

            {debug === "true" && (
                <div
                    style={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignContent: "center",
                        alignItems: "flex-start",
                        overflow: "hidden",
                    }}
                >
                    <div
                        style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", flexWrap: "wrap" }}
                    >
                        <b>Debug - Post Payment Setup</b>
                        <p>App ID: {appId}</p>
                        <p>Organization ID: {organizationId}</p>
                    </div>
                    <div style={{ display: "flex", flexDirection: "row", margin: "20px", padding: "10px" }}>
                        <Button label="Print App" onClick={() => console.log(app)} />
                    </div>
                </div>
            )}
        </>
    );
};
